import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, apiErrorMessage } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import sfx from "../game/sfx";

const SEGMENT_COLORS = [
  "#2ecc71", "#f7931a", "#00aaff", "#bb55ff",
  "#ff5588", "#ffdd00", "#00ffcc", "#a36bff",
];
const SPIN_MS = 4200;

function drawWheel(canvas, prizes, angleRad) {
  const ctx = canvas.getContext("2d");
  const size = canvas.width;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 12;
  ctx.clearRect(0, 0, size, size);
  const n = prizes.length;
  const step = (Math.PI * 2) / n;

  for (let i = 0; i < n; i++) {
    const start = angleRad + i * step;
    const end = start + step;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, r, start, end);
    ctx.closePath();
    ctx.fillStyle = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
    ctx.fill();
    ctx.strokeStyle = "rgba(15,15,30,0.7)";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Label
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(start + step / 2);
    ctx.textAlign = "right";
    ctx.textBaseline = "middle";
    ctx.font = "bold 11px 'Press Start 2P', monospace";
    ctx.fillStyle = "#0f0f1e";
    ctx.fillText(prizes[i].label, r - 12, 3);
    ctx.restore();
  }

  // Center hub
  ctx.beginPath();
  ctx.arc(cx, cy, 24, 0, Math.PI * 2);
  ctx.fillStyle = "#0f0f1e";
  ctx.fill();
  ctx.strokeStyle = "#2ecc71";
  ctx.lineWidth = 3;
  ctx.stroke();

  // Pointer (top)
  ctx.beginPath();
  ctx.moveTo(cx, 4);
  ctx.lineTo(cx - 14, 32);
  ctx.lineTo(cx + 14, 32);
  ctx.closePath();
  ctx.fillStyle = "#f7931a";
  ctx.fill();
  ctx.strokeStyle = "#0f0f1e";
  ctx.lineWidth = 2;
  ctx.stroke();
}

export default function WheelOfRewards({ onClose }) {
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | spinning | result
  const [result, setResult] = useState(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const tickRef = useRef(0);

  const prizes = useMemo(() => status?.prize_table || [], [status]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/wheel/status");
      setStatus(data);
    } catch (err) {
      // silent
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Draw initial wheel once we have the prize table
  useEffect(() => {
    if (!canvasRef.current || prizes.length === 0) return;
    drawWheel(canvasRef.current, prizes, 0);
  }, [prizes]);

  const animateTo = useCallback((winnerIndex) => {
    const canvas = canvasRef.current;
    if (!canvas) return Promise.resolve();
    const n = prizes.length;
    const step = (Math.PI * 2) / n;
    // Pointer at top = angle -PI/2. Make winning segment center land under pointer.
    const segmentCenter = winnerIndex * step + step / 2;
    const targetOffset = -Math.PI / 2 - segmentCenter;
    const spins = 5; // full rotations for drama
    const finalAngle = targetOffset + spins * Math.PI * 2;
    const start = performance.now();
    return new Promise((resolve) => {
      const tick = (now) => {
        const t = Math.min(1, (now - start) / SPIN_MS);
        const ease = 1 - Math.pow(1 - t, 3);
        const angle = finalAngle * ease;
        drawWheel(canvas, prizes, angle);
        // sfx tick
        const nextTick = Math.floor(ease * 30);
        if (nextTick !== tickRef.current) {
          tickRef.current = nextTick;
          sfx.play("wheel_tick");
        }
        if (t < 1) rafRef.current = requestAnimationFrame(tick);
        else resolve();
      };
      rafRef.current = requestAnimationFrame(tick);
    });
  }, [prizes]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  const spin = async () => {
    if (phase !== "idle" || !status?.eligible) return;
    setPhase("spinning");
    try {
      const { data } = await api.post("/wheel/spin");
      const idx = prizes.findIndex((p) => p.id === data.prize.id);
      await animateTo(idx >= 0 ? idx : 0);
      sfx.play("wheel_win");
      setResult(data.prize);
      setPhase("result");
      await refreshUser();
    } catch (err) {
      toast.error(apiErrorMessage(err));
      setPhase("idle");
    }
  };

  if (!status) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-[rgba(6,10,15,0.85)] backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      data-testid="wheel-modal"
    >
      <div className="relative w-full max-w-[380px] bg-[#0e1a28] border border-[rgba(0,255,136,0.35)] rounded-2xl p-5 flex flex-col items-center gap-4"
           style={{ boxShadow: "0 0 40px rgba(0,255,136,0.25)" }}>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="wheel-close-btn"
          className="absolute top-3 right-3 text-[#8b95a9] hover:text-white w-8 h-8 rounded-full border border-[rgba(255,255,255,0.12)]"
        >
          ✕
        </button>
        <h2 className="font-pixel text-[12px] text-[#f7931a] text-glow-gold">
          🎰 WHEEL OF REWARDS
        </h2>
        <p className="text-[11px] text-[#8b95a9] text-center">
          {status.eligible
            ? `${status.streak_days}-day streak milestone unlocked! Spin for a free reward.`
            : `Reach day ${status.threshold} of streak to unlock.`}
        </p>

        <canvas
          ref={canvasRef}
          width={320}
          height={320}
          className="w-[280px] h-[280px]"
          data-testid="wheel-canvas"
        />

        {phase !== "result" ? (
          <button
            type="button"
            onClick={spin}
            disabled={!status.eligible || phase === "spinning"}
            data-testid="wheel-spin-btn"
            className="cb-neon-btn text-[10px] py-3 px-8 w-full"
          >
            {phase === "spinning" ? "Spinning…" : status.eligible ? "SPIN NOW" : "Locked"}
          </button>
        ) : (
          <div className="flex flex-col items-center gap-3 w-full">
            <div className="font-pixel text-[11px] text-[#2ecc71] text-glow-green text-center" data-testid="wheel-result-label">
              🎉 {result?.label}
            </div>
            {result?.bc_earned ? (
              <div className="text-[13px] text-[#f7931a]">+{result.bc_earned} BC credited to your wallet</div>
            ) : result?.item_id ? (
              <div className="text-[13px] text-[#00aaff]">NFT added to your cosmetics!</div>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              data-testid="wheel-done-btn"
              className="cb-neon-btn text-[10px] py-3 px-8 w-full"
            >
              Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
